import { BaseViewStore } from 'core/stores';
import { RestockDateForm } from 'modules/order/forms';
import { RestockDateStore } from 'modules/restock-date/stores';
import { action, makeObservable, observable } from 'mobx';
import { channelType, sourceOfChange } from 'common/constants';
import { formatDate, isCurrentDateGreater, notificationError } from 'core/utils';
import moment from 'moment';
import _ from 'lodash';

class RestockDateViewStore extends BaseViewStore {
	@observable showScheduleRestockDate = false;
	@observable disableSaveButton = false;
	@observable errorMessage = '';
	itemType;
	isChannelDate = false;
	showScheduledRestockDatePassNote = false;
	scheduledRestockDate = null;
	itemTypes = {
		channel: 0,
		product: 1,
	};

	form = new RestockDateForm({
		onSuccess: async () => {
			this.createOrUpdateRestockDate();
		},
	});

	constructor(rootStore, { parentViewStore }) {
		super(rootStore);
		makeObservable(this);
		this.parentViewStore = parentViewStore;
		this.restockDateStore = new RestockDateStore(rootStore);
		this.setItemAndItemStore(rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		const locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		const localeData = moment.localeData();
		this.datePickerFormat = localeData.longDateFormat('L');
		if (this.item.productInventoryRestockDate) {
			let restockDate = this.item.productInventoryRestockDate.restockDate;
			this.form.$('restockDate').value = restockDate
				? moment(this.item.productInventoryRestockDate.restockDate).format('MM/DD/YYYY')
				: null;
			if (this.item.productInventoryRestockDate.scheduledRestockDateTime) {
				this.setInitialScheduleRestockDate();
			} else {
				this.form.$('scheduledRestockDate').value = null;
			}
		}
		this.resume();
	};

	setInitialScheduleRestockDate = () => {
		const scheduledDateLocalString = formatDate(
			this.item.productInventoryRestockDate.scheduledRestockDateTime,
			true,
			'LLL'
		);

		// If scheduled date has passed current date, form field should not be populated and note should be raised on restock modal.
		if (moment(scheduledDateLocalString) > new Date()) {
			this.form.$('scheduledRestockDate').value = formatDate(
				this.item.productInventoryRestockDate.scheduledRestockDateTime,
				true,
				'LLL'
			);
			this.showScheduleRestockDate = true;
		} else {
			this.scheduledRestockDate = scheduledDateLocalString;
			this.showScheduledRestockDatePassNote = true;
		}
	};

	@action
	setShowScheduleRestockDate = showScheduleRestockDate => {
		this.showScheduleRestockDate = showScheduleRestockDate;
	};

	@action
	createOrUpdateRestockDate = async () => {
		this.suspend();
		try {
			const { restockDate, scheduledRestockDate } = this.form.values();
			if (!this.showScheduleRestockDate || !this.checkForErrors()) {
				if (this.item.productInventoryRestockDateId && !this.isChannelDate) {
					await this.updateExistingRestockDate(restockDate, scheduledRestockDate);
				} else {
					await this.createNewRestockDate(restockDate, scheduledRestockDate);
				}
				this.parentViewStore.closeEditRestockDateModal();
				this.rootStore.notificationStore.success(['RESTOCKDATE.SUCCESSFULSET']);
				this.parentViewStore.initialize();
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}

		this.resume();
	};

	updateExistingRestockDate = async (restockDate, scheduledRestockDate) => {
		await this.restockDateStore.updateRestockDate(
			{
				id: this.item.id,
				productInventoryRestockDate: {
					id: this.item.productInventoryRestockDateId,
					restockDate: restockDate
						? moment(restockDate)
								.utc(true)
								.format()
						: null,
					scheduledRestockDateTime:
						this.showScheduleRestockDate && scheduledRestockDate ? moment(scheduledRestockDate).format() : null,
				},
			},
			this.itemType == this.itemTypes.channel
				? sourceOfChange.updateRestockDateChannel
				: sourceOfChange.updateRestockDateProduct
		);
	};

	createNewRestockDate = async (restockDate, scheduledRestockDate) => {
		let result = await this.restockDateStore.createRestockDate(
			{
				id: this.item.id,
				productInventoryRestockDate: {
					restockDate: restockDate
						? moment(restockDate)
								.utc(true)
								.format()
						: null,
					scheduledRestockDateTime:
						this.showScheduleRestockDate && scheduledRestockDate ? moment(scheduledRestockDate).format() : null,
				},
				itemType: this.itemType,
			},
			this.itemType == this.itemTypes.channel
				? sourceOfChange.createRestockDateChannel
				: sourceOfChange.createRestockDateProduct
		);
		if (result != null) {
			this.item.productInventoryRestockDate = result;
			this.item.productInventoryRestockDateId = result.id;
		}
	};

	@action
	setItemAndItemStore = () => {
		if (this.parentViewStore.constructor.name === 'ChannelEditViewStore') {
			this.item = this.parentViewStore.channel;
			this.itemType = this.itemTypes.channel;
		} else if (this.parentViewStore.constructor.name === 'ProductDetailViewStore') {
			this.item = this.parentViewStore.product;
			this.itemType = this.itemTypes.product;

			if (this.parentViewStore.product.productInventoryRestockDate == null) {
				// If product doesn't have restock date, set restock date from channel in runtime.
				const productVirtualSku = _.find(this.parentViewStore.product.productVirtualSkus, productVirtualSku => {
					return productVirtualSku.channel.channelType.name === channelType.amazon;
				});
				this.item.productInventoryRestockDate = productVirtualSku.channel.productInventoryRestockDate;
				this.isChannelDate = true;
			}
		}
		// If restock date has passed, clear values from form.
		// Inside database it will be cleared once update inventory job is triggered for certain Amazon sku.
		this.checkValidityOfRestockDate();
	};

	checkValidityOfRestockDate = () => {
		if (this.item.productInventoryRestockDate != null && this.item.productInventoryRestockDate.restockDate) {
			if (isCurrentDateGreater(this.item.productInventoryRestockDate.restockDate)) {
				this.item.productInventoryRestockDate.restockDate = null;
				this.item.productInventoryRestockDate.scheduledRestockDateTime = null;
			}
		}
	};

	@action
	onChange = value => {
		this.form.$('scheduledRestockDate').clear();
		if (value) {
			this.form.$('scheduledRestockDate').set(value.toUTCString());
		} else {
			this.showScheduleRestockDate = false;
		}
		this.checkForErrors();
	};

	@action
	checkForErrors = () => {
		const { restockDate, scheduledRestockDate } = this.form.values();

		if (restockDate && isCurrentDateGreater(restockDate)) {
			// Restock date cannot be in the past.
			this.rootStore.notificationStore.error(['RESTOCKDATE.RESTOCKDATEERROR']);
			this.disableSaveButton = true;
			return;
		}

		if (scheduledRestockDate && !restockDate) {
			// If scheduled date is picked, restock date should be picked as well.
			this.rootStore.notificationStore.error(['RESTOCKDATE.REQUIREDFIELD']);
			this.disableSaveButton = true;
			return;
		}

		if (restockDate && scheduledRestockDate) {
			// Scheduled restock date cannot be larger than restock date.
			if (moment(moment(scheduledRestockDate).format('MM/DD/YYYY')) > moment(restockDate)) {
				this.errorMessage = 'RESTOCKDATE.DATERANGEERRORMESSAGE';
				this.disableSaveButton = true;
				return;
			} else {
				this.errorMessage = '';
				this.disableSaveButton = false;
				return;
			}
		}

		this.errorMessage = '';
		this.disableSaveButton = false;
	};
}

export default RestockDateViewStore;



// WEBPACK FOOTER //
// ./src/modules/restock-date/stores/RestockDateViewStore.js